import style from './MarkdownWrapper.scss';

import cx from 'classnames';
import React from 'react';
import Markdown from 'react-markdown';

import { HeadingRenderer } from './HeadingRenderer/HeadingRenderer';
import { LinkRenderer } from './LinkRenderer';
import { IRootProps, RootRenderer } from './RootRenderer';

type MarkdownProps = Pick<Markdown.ReactMarkdownProps, Exclude<keyof Markdown.ReactMarkdownProps, 'className'>> & {
  innerRef?: React.Ref<HTMLElement>;
  dataTestLinkAttr?: string;
};

export type IMarkdownWrapperProps<E extends HTMLElement> = MarkdownProps & IRootProps<E>;

export class MarkdownWrapper<E extends HTMLElement = HTMLDivElement> extends React.PureComponent<IMarkdownWrapperProps<E>> {
  static defaultProps = { tagName: 'div' as keyof React.ReactHTML };

  render() {
    const {
      // react-markdown props
      source,
      sourcePos,
      rawSourcePos,
      escapeHtml,
      skipHtml,
      allowNode,
      allowedTypes,
      disallowedTypes,
      transformImageUri,
      transformLinkUri,
      unwrapDisallowed,
      renderers,
      astPlugins,
      plugins,
      dataTestLinkAttr,
      // html props
      className,

      // others (html props)
      ...otherProps
    } = this.props;

    const markdownProps: Markdown.ReactMarkdownProps = {
      source,
      sourcePos,
      rawSourcePos,
      escapeHtml,
      skipHtml,
      allowNode,
      allowedTypes,
      disallowedTypes,
      transformLinkUri,
      transformImageUri,
      unwrapDisallowed,
      renderers,
      astPlugins,
      plugins,
    };

    // blacklist of props to remove from spread
    ['entry', 'field' ].forEach(key => delete otherProps[key]);

    return (
      <Markdown
        {...markdownProps}
        renderers={{
          ...renderers,

          // to work around a bug with the default div renderer not spreading all the props
          // use a closure, also allows for tag customization
          root: (rootProps: any) => <RootRenderer className={cx(style.wrapper, className)} {...rootProps} {...otherProps} />,
          heading: HeadingRenderer,
          // todo: pass component directly when react-markdown supports forwardRef components
          // https://github.com/rexxars/react-markdown/issues/244
          link: linkProps => <LinkRenderer {...linkProps} {...Boolean(dataTestLinkAttr) && { 'data-test': dataTestLinkAttr }} />,
        }}
      />
    );
  }
}
